import { useState, useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { Gift, Sparkles, Lock } from "lucide-react";
import { CONTENT } from "@/content";

const floaters = Array.from({ length: 10 });

function getTargetTs() {
  const { month, day, hourUTC, year } = CONTENT.birthday;
  // Pinned year keeps the site open forever after Annie's birthday passes.
  // Falls back to the current year if `year` isn't configured.
  const y = year || new Date().getUTCFullYear();
  return Date.UTC(y, month - 1, day, hourUTC, 0, 0);
}

function isPreview() {
  if (typeof window === "undefined") return false;
  return new URLSearchParams(window.location.search).get("preview") === "1";
}

function breakdown(diff) {
  const s = Math.max(0, Math.floor(diff / 1000));
  return {
    days: Math.floor(s / 86400),
    hours: Math.floor((s % 86400) / 3600),
    mins: Math.floor((s % 3600) / 60),
    secs: s % 60,
  };
}

const pad = (n) => String(n).padStart(2, "0");

export default function RevealGate({ onOpen, onUnlock }) {
  const [target] = useState(getTargetTs);
  const [now, setNow] = useState(Date.now());
  const preview = isPreview();

  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(id);
  }, []);

  const diff = target - now;
  const locked = diff > 0 && !preview;
  const t = breakdown(diff);

  // Fire confetti the moment the countdown hits zero (before she taps).
  const wasLockedRef = useRef(locked);
  const firedRef = useRef(false);
  useEffect(() => {
    if (wasLockedRef.current && !locked && !firedRef.current) {
      firedRef.current = true;
      if (onUnlock) onUnlock();
    }
    wasLockedRef.current = locked;
  }, [locked, onUnlock]);

  const units = [
    { label: "days", value: t.days },
    { label: "hours", value: pad(t.hours) },
    { label: "mins", value: pad(t.mins) },
    { label: "secs", value: pad(t.secs) },
  ];

  return (
    <div
      className="gate fixed inset-0 z-50 flex items-center justify-center overflow-hidden"
      style={{ backgroundColor: "#0d0608", cursor: locked ? "default" : "pointer" }}
      onClick={locked ? undefined : onOpen}
      data-testid="reveal-gate"
    >
      {/* soft radial light */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background:
            "radial-gradient(60% 60% at 50% 40%, rgba(232,180,184,0.12), transparent 70%)",
        }}
      />

      {/* floating petals */}
      {floaters.map((_, i) => (
        <motion.div
          key={i}
          className="absolute rounded-full pointer-events-none"
          style={{
            left: `${(i * 97) % 100}%`,
            width: 6 + (i % 4) * 3,
            height: 6 + (i % 4) * 3,
            background: i % 2 ? "rgba(232,180,184,0.5)" : "rgba(212,175,55,0.4)",
          }}
          initial={{ y: "110vh", opacity: 0 }}
          animate={{
            y: "-10vh",
            opacity: [0, 1, 1, 0],
            x: [0, i % 2 ? 40 : -40, 0],
          }}
          transition={{
            duration: 9 + (i % 5),
            repeat: Infinity,
            delay: i * 0.7,
            ease: "linear",
          }}
        />
      ))}

      <div className="relative text-center px-6">
        <motion.p
          className="font-script text-2xl md:text-3xl mb-5"
          style={{ color: "var(--rose)" }}
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.3, duration: 0.9 }}
          data-testid="gate-eyebrow"
        >
          {CONTENT.gate.eyebrow}
        </motion.p>

        <motion.h1
          className="font-serif-d tracking-tightest text-6xl md:text-8xl font-light mb-10"
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5, duration: 1 }}
        >
          {CONTENT.gate.title}
        </motion.h1>

        {locked ? (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7, duration: 0.9 }}
            data-testid="countdown"
          >
            <div className="flex items-center justify-center gap-3 md:gap-5 mb-8">
              {units.map((u) => (
                <div
                  key={u.label}
                  className="glass rounded-2xl px-4 py-4 md:px-6 md:py-5 min-w-[68px] md:min-w-[92px]"
                  data-testid={`countdown-${u.label}`}
                >
                  <div
                    className="font-serif-d text-4xl md:text-6xl font-light leading-none tabular-nums"
                    style={{ color: "var(--alabaster)" }}
                  >
                    {u.value}
                  </div>
                  <div
                    className="font-body text-[10px] md:text-xs uppercase tracking-[0.25em] mt-2"
                    style={{ color: "var(--muted-rose)" }}
                  >
                    {u.label}
                  </div>
                </div>
              ))}
            </div>

            <div
              className="flex items-center justify-center gap-2 text-xs uppercase tracking-[0.3em]"
              style={{ color: "var(--muted-rose)" }}
            >
              <Lock size={13} strokeWidth={1.5} />
              unlocks on {CONTENT.birthday.label}
            </div>
          </motion.div>
        ) : (
          <>
            <motion.button
              onClick={onOpen}
              data-testid="open-gift-button"
              className="group relative inline-flex items-center gap-3 rounded-full px-9 py-4 font-body text-sm uppercase tracking-[0.25em]"
              style={{ color: "#0d0608", backgroundColor: "var(--rose)" }}
              initial={{ opacity: 0, scale: 0.9 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.2, duration: 0.8 }}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.97 }}
            >
              <motion.span
                className="absolute -inset-2 rounded-full pointer-events-none"
                style={{ border: "1px solid rgba(232,180,184,0.4)" }}
                animate={{ scale: [1, 1.15, 1], opacity: [0.6, 0, 0.6] }}
                transition={{ duration: 2.4, repeat: Infinity }}
              />
              <Gift size={18} strokeWidth={1.5} />
              {CONTENT.gate.cta}
            </motion.button>

            <motion.div
              className="mt-10 flex items-center justify-center gap-2 text-xs uppercase tracking-[0.3em]"
              style={{ color: "var(--muted-rose)" }}
              initial={{ opacity: 0 }}
              animate={{ opacity: 0.8 }}
              transition={{ delay: 0.6, duration: 1 }}
            >
              <Sparkles size={13} strokeWidth={1.5} />
              our song awaits
            </motion.div>
          </>
        )}
      </div>
    </div>
  );
}
